import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { api, parseUtmParams } from "@/lib/api";

const INTEREST_OPTIONS = [
  { value: "csak_erdekel", label: "Csak érdekel" },
  { value: "1_zsak", label: "1 zsák érdekel" },
  { value: "2_3_zsak", label: "2–3 zsák érdekel" },
  { value: "4_10_zsak", label: "4–10 zsák érdekel" },
  { value: "10plusz_zsak", label: "10+ zsák érdekel" },
  { value: "nem_tudom", label: "Még nem tudom" },
];

export default function SubscribeFormGB() {
  const [form, setForm] = useState({
    first_name: "", email: "", phone: "", interest_level: "",
    marketing_consent: false, privacy_consent: false,
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [copiedRef, setCopiedRef] = useState(false);
  const [refCode, setRefCode] = useState(null);

  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const r = sp.get("ref");
    if (r) setRefCode(r.toUpperCase());
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim()) return toast.error("Add meg a keresztneved");
    if (!form.email.trim()) return toast.error("Add meg az email címed");
    if (!form.interest_level) return toast.error("Válassz érdeklődési szintet");
    if (!form.privacy_consent) return toast.error("Fogadd el az adatkezelési tájékoztatót");
    setLoading(true);
    try {
      const utm = parseUtmParams();
      const { data } = await api.post("/subscribe", { ...form, ...utm, ref: refCode });
      setResult(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Hiba történt. Próbáld újra.");
    } finally {
      setLoading(false);
    }
  };

  const copy = async (text, setter) => {
    try { await navigator.clipboard.writeText(text); setter(true); setTimeout(() => setter(false), 1500); }
    catch { toast.error("Nem sikerült másolni."); }
  };

  if (result) {
    const link = `${window.location.origin}/?ref=${result.coupon_code}`;
    return (
      <div className="form-card" data-testid="subscribe-success-card">
        <p className="eyebrow" style={{ color: "var(--moss)" }}>● Sikeres feliratkozás</p>
        <h3 className="display" style={{ fontSize: "clamp(2.6rem, 10vw, 4rem)", margin: "0 0 16px" }}>Megvan! 🌱</h3>
        <p className="muted" style={{ margin: "0 0 20px" }}>
          Köszönjük, {result.first_name}! Az első vásárlásodhoz <b>{result.discount_percent}%</b> kedvezményt kapsz.
        </p>
        <div className="code-box-gb" data-testid="coupon-code-display">{result.coupon_code}</div>
        <button type="button" className="btn btn-ghost btn-block" style={{ marginTop: 12 }} onClick={() => copy(result.coupon_code, setCopied)} data-testid="coupon-copy-button">
          {copied ? "Kimásolva ✓" : "Kód másolása"}
        </button>

        <div style={{ marginTop: 28, padding: 20, border: "1.5px solid var(--line-strong)", borderRadius: "var(--radius)", background: "var(--card)" }}>
          <p className="mono" style={{ color: "var(--kraft-3)", marginBottom: 8 }}>HOZZ BARÁTOT · +5% EXTRA</p>
          <p style={{ fontSize: ".92rem", margin: "0 0 12px" }}>
            Oszd meg az egyedi linked. Minden feliratkozó barátod után +5% kedvezményt adunk a kódodhoz (max 25%).
          </p>
          <div className="mono" style={{ wordBreak: "break-all", padding: "10px 12px", background: "var(--paper)", border: "1px solid var(--line)", fontSize: ".78rem" }} data-testid="referral-link">
            {link}
          </div>
          <button type="button" className="btn btn-ghost btn-block" style={{ marginTop: 12 }} onClick={() => copy(link, setCopiedRef)} data-testid="referral-copy-button">
            {copiedRef ? "Kimásolva ✓" : "Ajánlói link másolása"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <form className="form-card" onSubmit={submit} data-testid="growbag-subscribe-form" noValidate>
      {refCode && (
        <div style={{ padding: "12px 14px", marginBottom: 20, border: "1.5px solid var(--kraft)", background: "rgba(196,154,108,.15)", borderRadius: "var(--radius)" }}>
          <p className="mono" style={{ color: "var(--kraft-3)" }}>● BARÁTOD AJÁNLOTT · 15% kedvezmény</p>
          <p style={{ fontSize: ".88rem", margin: "4px 0 0", color: "var(--soil)" }}>Ajánló: {refCode}</p>
        </div>
      )}
      <div className="field">
        <label className="field-label" htmlFor="firstName">Keresztnév</label>
        <input id="firstName" className="input-gb" value={form.first_name} onChange={(e) => update("first_name", e.target.value)} maxLength={60} required data-testid="subscribe-name-input" />
      </div>
      <div className="field">
        <label className="field-label" htmlFor="email">Email</label>
        <input id="email" type="email" className="input-gb" value={form.email} onChange={(e) => update("email", e.target.value)} maxLength={254} required data-testid="subscribe-email-input" />
      </div>
      <div className="field">
        <label className="field-label" htmlFor="phone">Telefonszám <span className="opt">opcionális</span></label>
        <input id="phone" type="tel" className="input-gb" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="+36 30 123 4567" data-testid="subscribe-phone-input" />
      </div>
      <div className="field">
        <fieldset className="choices" data-testid="subscribe-interest-select">
          <legend className="field-label">Mennyire érdekel?</legend>
          {INTEREST_OPTIONS.map((o) => (
            <label key={o.value} className="choice">
              <input type="radio" name="interestLevel" value={o.value} checked={form.interest_level === o.value} onChange={() => update("interest_level", o.value)} data-testid={`interest-option-${o.value}`} />
              <span>{o.label}</span>
            </label>
          ))}
        </fieldset>
        <p style={{ fontSize: ".84rem", color: "var(--muted)", margin: "10px 0 0" }}>Nem rendelés és nem kötelezettség — csak segít, hogy tudjuk, mekkora adagot érdemes készíteni.</p>
      </div>
      <div style={{ marginTop: 28 }}>
        <label className="check-gb">
          <input type="checkbox" checked={form.privacy_consent} onChange={(e) => update("privacy_consent", e.target.checked)} data-testid="subscribe-privacy-consent" required />
          <span>Elolvastam az <a href="/adatkezeles" target="_blank" rel="noopener" style={{ textUnderlineOffset: 2 }}>adatkezelési tájékoztatót</a>, és kérem a kuponkódomat. <span className="muted">(kötelező)</span></span>
        </label>
        <label className="check-gb">
          <input type="checkbox" checked={form.marketing_consent} onChange={(e) => update("marketing_consent", e.target.checked)} data-testid="subscribe-marketing-consent" />
          <span>Kérek alkalmanként GrowBag hírlevelet is. <span className="muted">(opcionális)</span></span>
        </label>
      </div>
      <button type="submit" className="btn btn-primary btn-block" style={{ marginTop: 12, minHeight: 60, fontSize: "1.02rem" }} disabled={loading} data-testid="subscribe-submit-button">
        {loading ? "Küldés..." : `Feliratkozom és kérem a ${refCode ? 15 : 10}% kedvezményt`} <span className="arrow">→</span>
      </button>
      <p className="muted" style={{ fontSize: ".82rem", margin: "14px 0 0", textAlign: "center" }}>Nem küldünk spamet. Bármikor leiratkozhatsz.</p>
    </form>
  );
}
